import { useRef } from "react";

import type { BaseTextFieldProps, TextFieldState } from "../text-field";
import { Input } from "./input";
import {
  calculateCursorPosition,
  formatByMask,
  normalizePhoneInput,
} from "./utils";

type PhoneFieldState = TextFieldState;

type PhoneFieldProps = BaseTextFieldProps<PhoneFieldState> & {
  pattern: string;
  countryCode: string;
};

export const PhoneField = ({
  field,
  pattern,
  countryCode,
  ...props
}: PhoneFieldProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  const maxDigits = pattern.split("").filter((c) => c === "#").length;
  const countryCodeDigits = countryCode.replace("+", "");
  const maxTotalDigits = countryCodeDigits.length + maxDigits;

  return (
    <Input
      {...props}
      ref={inputRef}
      onFocus={() => {
        const caretPosition = inputRef.current?.selectionStart ?? 0;

        if (field.value === "" || caretPosition <= countryCode.length + 2) {
          const newCursor = calculateCursorPosition({
            caretBeforeChange: countryCode.length + 2,
            maskedValue: field.value,
            defaultPosition: countryCode.length + 2,
            direction: "forward",
          });
          requestAnimationFrame(() => {
            inputRef.current?.setSelectionRange(newCursor, newCursor);
          });
        }
      }}
      field={{
        ...field,
        value: formatByMask(field.value, pattern, countryCode),
        onChange: (input) => {
          if ("value" in input) {
            const value = input.value;
            const inputBeforeChange = value;
            const caretBeforeChange =
              inputRef.current?.selectionStart ?? value.length;

            const normalized = normalizePhoneInput(
              inputBeforeChange,
              countryCode,
            );
            const limitedDigits = normalized
              .replace(/\D/g, "")
              .slice(0, maxTotalDigits);

            const finalValue = `+${limitedDigits}`;
            field.onChange({ ...field, value: finalValue });

            const newCursor = calculateCursorPosition({
              caretBeforeChange: caretBeforeChange,
              maskedValue: formatByMask(
                inputBeforeChange,
                pattern,
                countryCode,
              ),
              defaultPosition: countryCode.length + 2,
              direction: getDirection(field.value, finalValue),
            });

            requestAnimationFrame(() => {
              inputRef.current?.setSelectionRange(newCursor, newCursor);
            });
          }
        },
      }}
      onPaste={(event) => {
        const pasteData = event.clipboardData.getData("Text");
        const normalized = normalizePhoneInput(pasteData, countryCode);
        event.preventDefault();
        field.onChange({ ...field, value: normalized });
      }}
    />
  );
};

const getDirection = (
  previous: string,
  current: string,
): "forward" | "backward" => {
  if (current.length > previous.length) return "forward";
  return "backward";

  // если длина не изменилась — можно посмотреть по caret (на всякий случай)
};
